/**
 * Transport row (SPEC §6.6): ▶ Play (the controller's shared button) / ■ Stop /
 * ● Record (armed state) / metronome toggle / position `bar.step` /
 * "Sound off — press Play" pill until audio is unlocked / Undo (Cmd+Z) /
 * the first-load copy that hides after the first agent ledger entry.
 */
import type { Store } from '../state/store-api';
import type { Controller, Engine } from '../app/controller';
import { splitStep } from '../types';
import { ledger } from '../webmcp/ledger';
import { h, injectStyle } from './dom';
import { undoOnce } from './shortcuts';

/** Dependencies for `mountTransport()`. */
export interface TransportDeps {
  store: Store;
  controller: Controller;
  engine: Pick<Engine, 'isPlaying' | 'onStep' | 'position'>;
}

/** Mounted transport row: root element, position hook, dispose. */
export interface TransportHandle {
  el: HTMLElement;
  /** Update the position readout for absolute `step`. */
  setStep(step: number): void;
  dispose(): void;
}

/** The first-load prompt shown until the agent does something. */
export const FIRST_LOAD_HINT = 'Press Play. Then ask your agent: “what goes under this?”';

const CSS = `
.bm-transport{display:flex;align-items:center;gap:.5rem;flex-wrap:wrap;min-width:0;padding:.6rem .75rem;border:1px solid var(--border);
  border-radius:var(--radius-l);background:var(--surface-1)}
.bm-transport__group{display:flex;align-items:center;gap:.4rem;flex-wrap:wrap}
.bm-transport__play[aria-pressed="true"]{background:var(--accent);color:var(--accent-fg);border-color:transparent;box-shadow:0 0 0 3px var(--agent-soft)}
.bm-transport__rec[aria-pressed="true"]{background:var(--danger-soft);border-color:var(--danger);color:var(--fg)}
.bm-transport__rec[aria-pressed="true"] .bm-transport__dot{animation:bm-blink 1s steps(2,start) infinite}
.bm-transport__dot{color:var(--danger)}
.bm-transport__pos{font-family:var(--font-mono);font-variant-numeric:tabular-nums;min-width:3.2em;text-align:center;padding:.3rem .5rem;
  border:1px solid var(--border);border-radius:var(--radius-s);background:var(--surface-2);color:var(--fg)}
.bm-transport__right{margin-left:auto;display:flex;align-items:center;gap:.5rem;flex-wrap:wrap;min-width:0}
@media (max-width:600px){.bm-transport__right{margin-left:0;width:100%}}
.bm-transport__hint{width:100%;margin:0;padding:.35rem .1rem 0;font-size:.85rem;color:var(--fg-muted)}
.bm-transport__hint b{color:var(--fg);font-weight:600}
`;

/** Mount the transport row into `container`. */
export function mountTransport(container: HTMLElement, deps: TransportDeps): TransportHandle {
  injectStyle('bm-transport-css', CSS);
  const { store, controller, engine } = deps;
  const disposers: Array<() => void> = [];

  const playBtn = controller.playButton();
  playBtn.classList.add('bm-transport__play');
  playBtn.setAttribute('aria-pressed', String(engine.isPlaying()));

  const stopBtn = h(
    'button',
    { type: 'button', class: 'bm-btn', 'data-stop': true, 'aria-label': 'Stop', 'aria-keyshortcuts': 'Escape' },
    h('span', { 'aria-hidden': 'true' }, '■'),
    ' Stop',
  );
  stopBtn.addEventListener('click', () => {
    controller.stop();
    syncPlaying();
  });

  const recBtn = h(
    'button',
    { type: 'button', class: 'bm-btn bm-btn--danger bm-transport__rec', 'data-record': true, 'aria-pressed': 'false', 'aria-keyshortcuts': 'R', title: 'Record the lead track live from the piano (R)' },
    h('span', { class: 'bm-transport__dot', 'aria-hidden': 'true' }, '●'),
    ' Record',
  );
  recBtn.addEventListener('click', () => {
    if (controller.isRecording()) controller.stopRecording();
    else void controller.startRecording();
  });

  const metBtn = h(
    'button',
    { type: 'button', class: 'bm-btn', 'data-metronome': true, 'aria-pressed': 'false', 'aria-label': 'Metronome', title: 'Metronome' },
    h('span', { 'aria-hidden': 'true' }, '⏱'),
    ' Click',
  );
  metBtn.addEventListener('click', () => {
    const metronome = !store.get().metronome;
    store.apply({ type: 'setGroove', metronome }, { by: 'human', label: `metronome ${metronome ? 'on' : 'off'}` });
  });

  const pos = h('output', { class: 'bm-transport__pos', 'data-position': true, 'aria-label': 'Position (bar.step)', 'aria-live': 'off' }, '1.1') as HTMLOutputElement;

  const audioPill = h(
    'span',
    { class: 'bm-pill bm-pill--warn', 'data-audio': true, role: 'status' },
    h('span', { class: 'bm-pill__dot' }),
    h('span', { class: 'bm-pill__text' }, 'Sound off — press Play'),
  );

  const undoBtn = h(
    'button',
    { type: 'button', class: 'bm-btn', 'data-undo': true, 'aria-keyshortcuts': 'Meta+Z Control+Z', title: 'Undo (Cmd+Z)' },
    h('span', { 'aria-hidden': 'true' }, '↶'),
    ' Undo',
  ) as HTMLButtonElement;
  undoBtn.addEventListener('click', () => {
    // Same path as Cmd+Z: an agent entry is undone through its ledger closure so the panel shows it as undone.
    const label = undoOnce(store);
    if (label !== null) store.logHuman({ kind: 'undo', detail: `undid ${label}`.slice(0, 80) });
  });

  const hint = h('p', { class: 'bm-transport__hint', 'data-hint': true }, 'Press Play. Then ask your agent: ', h('b', null, '“what goes under this?”'));

  const el = h(
    'div',
    { class: 'bm-transport', role: 'group', 'aria-label': 'Transport' },
    h('div', { class: 'bm-transport__group' }, playBtn, stopBtn, recBtn, metBtn),
    pos,
    h('div', { class: 'bm-transport__right' }, audioPill, undoBtn),
    hint,
  );

  // ---------- state sync ----------
  const syncPlaying = () => playBtn.setAttribute('aria-pressed', String(engine.isPlaying()));
  playBtn.addEventListener('click', () => queueMicrotask(syncPlaying));
  // Space / Esc / tools stop the transport without a click here: follow the controller.
  const offTransport = controller.onTransportChange?.(syncPlaying);
  if (offTransport) disposers.push(offTransport);

  const setStep = (step: number) => {
    const { bar, stepInBar } = splitStep(step);
    pos.value = `${bar + 1}.${stepInBar + 1}`;
    syncPlaying();
  };
  disposers.push(engine.onStep(setStep));

  disposers.push(
    controller.onAudioReady(() => {
      audioPill.hidden = true;
    }),
  );
  audioPill.hidden = controller.audioReady();

  disposers.push(
    controller.onModeChange((mode) => {
      recBtn.setAttribute('aria-pressed', String(mode === 'recording'));
      recBtn.title = mode === 'recording' ? 'Stop recording (R)' : 'Record the lead track live from the piano (R)';
      syncPlaying();
    }),
  );

  const syncStore = () => {
    metBtn.setAttribute('aria-pressed', String(store.get().metronome));
    undoBtn.disabled = store.undoStack().length === 0;
    const top = store.undoStack()[0];
    undoBtn.title = top ? `Undo: ${top.label} (Cmd+Z)` : 'Nothing to undo';
  };
  syncStore();
  disposers.push(store.subscribe(syncStore));

  const hideHintIfAgentActed = (entries: readonly { source: string }[]) => {
    if (entries.some((e) => e.source === 'agent')) hint.hidden = true;
  };
  disposers.push(ledger.subscribe(hideHintIfAgentActed));

  container.append(el);
  return {
    el,
    setStep,
    dispose() {
      for (const d of disposers) d();
      el.remove();
    },
  };
}
